import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import { GetSupportContractData, ProjectListItem } from 'apiClient/Api';

const money = (v?: number) =>
    Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const dateTime = (v?: string) => (v ? new Date(v).toLocaleString('de-DE') : '');

export interface SaveProjectValues {
    name: string;
    customerReference: string;
    customerName: string;
    supportContractId: string | null;
}

/** Autocomplete-Auswahl eines Support-Vertrags (liefert id + Kundendaten zurück). */
const ContractPicker: React.FC<{
    onPick: (contract: GetSupportContractData | null) => void;
    helperText?: string;
}> = ({ onPick, helperText }) => {
    const { api } = useApplicationContext();
    const [options, setOptions] = useState<GetSupportContractData[]>([]);
    const [input, setInput] = useState('');
    const [selected, setSelected] = useState<GetSupportContractData | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;
        const handle = setTimeout(() => {
            setLoading(true);
            api.supportContract
                .supportContractListSupportContracts(input ? { searchTerm: input } : {})
                .then((r) => active && setOptions(r.data ?? []))
                .catch(() => undefined)
                .finally(() => active && setLoading(false));
        }, 300);
        return () => {
            active = false;
            clearTimeout(handle);
        };
    }, [input, api]);

    return (
        <Autocomplete
            options={options}
            value={selected}
            loading={loading}
            getOptionLabel={(o) => [o.title, o.customerName].filter(Boolean).join(' – ') || o.id || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => {
                setSelected(v);
                onPick(v);
            }}
            onInputChange={(_, v) => setInput(v)}
            renderInput={(params) => (
                <TextField {...params} label="Support-Vertrag (optional)" size="small" helperText={helperText} />
            )}
        />
    );
};

/** Dialog zum Speichern als neues Projekt (POST). Name ist Pflicht. */
export const SaveProjectDialog: React.FC<{
    open: boolean;
    title: string;
    initial: SaveProjectValues;
    onClose: () => void;
    onSubmit: (values: SaveProjectValues) => Promise<void>;
}> = ({ open, title, initial, onClose, onSubmit }) => {
    const { t } = useTranslation();
    const [values, setValues] = useState<SaveProjectValues>(initial);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setValues(initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const patch = (p: Partial<SaveProjectValues>) => setValues((v) => ({ ...v, ...p }));

    const submit = async () => {
        if (!values.name.trim()) return;
        setSaving(true);
        try {
            await onSubmit({ ...values, name: values.name.trim() });
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label={t('calculation.store.name')}
                        size="small"
                        required
                        autoFocus
                        value={values.name}
                        onChange={(e) => patch({ name: e.target.value })}
                        error={!values.name.trim()}
                    />
                    <ContractPicker
                        helperText={t('calculation.store.contractHint')}
                        onPick={(c) =>
                            patch({
                                supportContractId: c?.id ?? null,
                                customerName: values.customerName || c?.customerName || '',
                                customerReference: values.customerReference || c?.customerId || '',
                            })
                        }
                    />
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label={t('calculation.store.customerName')}
                            size="small"
                            fullWidth
                            value={values.customerName}
                            onChange={(e) => patch({ customerName: e.target.value })}
                        />
                        <TextField
                            label={t('calculation.store.customerReference')}
                            size="small"
                            value={values.customerReference}
                            onChange={(e) => patch({ customerReference: e.target.value })}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel', 'Abbrechen')}</Button>
                <Button variant="contained" onClick={submit} disabled={saving || !values.name.trim()}>
                    {t('calculation.store.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** Dialog zum Laden eines gespeicherten Projekts aus der Liste (mit Löschen). */
export const LoadProjectDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    onLoad: (id: string) => Promise<void>;
}> = ({ open, onClose, onLoad }) => {
    const { t } = useTranslation();
    const { api, apiError } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    const [items, setItems] = useState<ProjectListItem[]>([]);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(() => {
        setLoading(true);
        api.calculator
            .projectsList()
            .then((r) => setItems(r.data ?? []))
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, apiError]);

    useEffect(() => {
        if (open) reload();
    }, [open, reload]);

    const remove = async (id: string, name: string) => {
        if (!window.confirm(t('calculation.store.deleteConfirm', { name }))) return;
        try {
            await api.calculator.projectsDelete(id);
            enqueueSnackbar(t('calculation.store.deleted'), { variant: 'success' });
            reload();
        } catch (e) {
            apiError(e);
        }
    };

    const sorted = useMemo(
        () => [...items].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')),
        [items],
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('calculation.store.loadTitle')}</DialogTitle>
            <DialogContent>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                {!loading && sorted.length === 0 && (
                    <Typography color="text.secondary">{t('calculation.store.empty')}</Typography>
                )}
                {sorted.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('calculation.store.name')}</TableCell>
                                <TableCell>{t('calculation.store.customerName')}</TableCell>
                                <TableCell align="right">{t('calculation.store.lastCalc')}</TableCell>
                                <TableCell>{t('calculation.store.updated')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((p) => (
                                <TableRow key={p.id} hover>
                                    <TableCell>{p.name}</TableCell>
                                    <TableCell>{p.customerName}</TableCell>
                                    <TableCell align="right">
                                        {p.lastCalculation ? (
                                            <Box>
                                                <Typography variant="body2">{money(p.lastCalculation.totalNet)}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {p.lastCalculation.positionCount} Pos.
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                –
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{dateTime(p.updatedAt)}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {p.updatedBy}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<FontAwesomeIcon icon={faFolderOpen} />}
                                            onClick={() => p.id && onLoad(p.id).then(onClose)}
                                        >
                                            {t('calculation.store.open')}
                                        </Button>
                                        <Tooltip title={t('calculation.store.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => p.id && remove(p.id, p.name || '')}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.close', 'Schließen')}</Button>
            </DialogActions>
        </Dialog>
    );
};
